import { useEffect, useId, useRef, useState } from 'react';
import { Lock, X, Eye, EyeOff, Chrome } from 'lucide-react';
import { EmailAuthProvider, reauthenticateWithCredential, reauthenticateWithPopup } from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { googleProvider } from '../firebaseClient';
import { apiDelete, apiGet } from '../utils/apiClient';
import { useModalFocus } from '../hooks/useModalFocus';
import { PlanErrorSummary } from './PlanErrorSummary';
import { formatBillingDate } from '../utils/planPricing';

/**
 * Delete account, in three steps:
 *   1. What gets deleted, tailored to the account (plan, household) from /user/me + /households/me
 *   2. Confirm it's you (password, or Google), then refresh the token so the server sees a recent
 *      sign-in. DELETE /api/account refuses (403 reauth-required) if the sign-in is older than 5 min.
 *   3. Type DELETE, then delete. Success signs out and lands on /account-deleted.
 *
 * Errors: inline next to the field AND an error summary at the top of the step, which takes focus.
 * Never toasts. Consequences are spelled out in words, not shown by colour alone.
 */

// Muted rose (palette) for the destructive action: white on it is 6.0:1, as text on white 6.0:1.
const ROSE = '#9A4D41';

const BUTTON = 'flex-1 min-h-[48px] py-3 rounded-full font-medium transition-colors disabled:opacity-60';
const BUTTON_SECONDARY = `${BUTTON} bg-white text-[#5F6B3F] border-2 border-[#5F6B3F] hover:bg-[#F0F3EC]`;
const BUTTON_DESTRUCTIVE = `${BUTTON} bg-[#9A4D41] text-white hover:bg-[#843F35] flex items-center justify-center gap-2`;
const BUTTON_PRIMARY = `${BUTTON} bg-[#AFBB8B] text-[#1F2814] hover:bg-[#9AA876] flex items-center justify-center gap-2`;

const WHAT_IS_DELETED = ['Your recipes', 'Your meal plans', 'Your shopping lists', 'Your photos', 'Your login'];

export function DeleteAccountDrawer({ isOpen, onClose, onDelete }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [summaryError, setSummaryError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authAttempts, setAuthAttempts] = useState(0);
  const [profile, setProfile] = useState(null);
  const [household, setHousehold] = useState(null);
  const panelRef = useRef(null);
  // Set once the person has re-entered their password (or re-signed in with Google) in this drawer.
  const justReauthenticated = useRef(false);
  const titleId = useId();
  const passwordErrorId = useId();
  const confirmErrorId = useId();
  const confirmHintId = useId();

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    (async () => {
      const [me, hh] = await Promise.all([
        apiGet('/user/me').catch(() => null),
        // Never block deletion because the household couldn't be read: just show no household line.
        apiGet('/households/me').catch(() => null),
      ]);
      if (!cancelled) {
        setProfile(me);
        setHousehold(hh);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  const resetAndClose = () => {
    justReauthenticated.current = false;
    setStep(1);
    setPassword('');
    setPasswordError('');
    setShowPassword(false);
    setConfirmText('');
    setConfirmError('');
    setSummaryError('');
    setAuthAttempts(0);
    onClose();
  };

  // No dismissing during the final step or while deleting (matches the backdrop).
  useModalFocus({
    isOpen,
    panelRef,
    onClose: resetAndClose,
    stepKey: step,
    canClose: (step === 1 || step === 2) && !loading,
  });

  if (!isOpen) return null;

  // ---- What this account has -----------------------------------------------------------------
  const tier = String(profile?.subscriptionTier || '').toLowerCase();
  const billing = String(profile?.subscriptionBilling || '').toLowerCase();
  const isAppleBilling = profile?.subscriptionType === 'apple';
  // Paying for Solo/Family themselves (not given it for free, not covered by someone's household).
  const isPaidPlan = (tier === 'solo' || tier === 'family') && billing !== 'free' && billing !== 'household';
  // On the free trial but already subscribed: the plan starts (and charging begins) when it ends.
  const isTrialSubscribed = tier === 'trial' && !!profile?.subscriptionPlan;
  const hasStripePlan = isPaidPlan || isTrialSubscribed;
  const planName = (isTrialSubscribed ? profile.subscriptionPlan : tier) === 'family' ? 'Family' : 'Solo';
  // Already cancelled: it runs to its end date (or, on the trial, won't start).
  const alreadyCancelled = hasStripePlan && profile?.subscriptionCancelled === true;
  const endText = formatBillingDate(
    isTrialSubscribed ? (profile?.trialEndDate ? new Date(profile.trialEndDate) : null) : profile?.subscriptionEndDate
  );
  // Only for people who are, or will be, charged. Plain trial users aren't, so it doesn't apply.
  const showCancelInstead = hasStripePlan && !alreadyCancelled;
  const isOwner = !!(household?.ownerUid && user?.uid && household.ownerUid === user.uid);
  const otherMembers = isOwner ? (household.members || []).filter((m) => m?.uid && m.uid !== household.ownerUid) : [];
  // Trial owners have Family features too, so their members lose them as well.
  const showMembersLine = otherMembers.length > 0 && (tier === 'family' || tier === 'trial');

  // ---- Re-authentication ------------------------------------------------------------------------
  const providerIds = Array.isArray(user?.providerData) ? user.providerData.map((p) => p?.providerId).filter(Boolean) : [];
  const hasPasswordProvider = providerIds.includes('password');
  const canReauthWithGoogle = !hasPasswordProvider && providerIds.includes('google.com');

  // A fresh token carries the new sign-in time, which DELETE /api/account checks.
  const afterReauth = async () => {
    await user.getIdToken(true);
    justReauthenticated.current = true;
    setSummaryError('');
    setStep(3);
  };

  const showPasswordError = (message) => {
    setPasswordError(message);
    setSummaryError(message);
  };

  const handleVerifyWithGoogle = async () => {
    setPasswordError('');
    setSummaryError('');
    setLoading(true);
    try {
      await reauthenticateWithPopup(user, googleProvider);
      await afterReauth();
    } catch (error) {
      const code = error?.code || '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        showPasswordError('Verification was cancelled. Please try again.');
      } else if (code === 'auth/user-mismatch' || code === 'auth/invalid-credential') {
        showPasswordError("That Google account doesn't match this one. Use the account you want to delete.");
      } else if (code === 'auth/popup-blocked') {
        showPasswordError('Your browser blocked the pop-up. Allow pop-ups for this site and try again.');
      } else {
        showPasswordError("We couldn't verify with Google. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPassword = async (event) => {
    event?.preventDefault();
    setPasswordError('');
    setSummaryError('');
    if (!password) {
      showPasswordError('Please enter your password.');
      return;
    }
    if (authAttempts >= 3) {
      showPasswordError('Too many attempts. Please wait a few minutes and try again.');
      return;
    }
    if (!user?.email) {
      showPasswordError("We couldn't check your account. Please sign out, sign back in and try again.");
      return;
    }
    setLoading(true);
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
      setPassword('');
      await afterReauth();
    } catch (error) {
      setAuthAttempts((n) => n + 1);
      const code = error?.code || '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        showPasswordError("That password isn't right. Please try again.");
      } else if (code === 'auth/too-many-requests') {
        showPasswordError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        showPasswordError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ---- Delete ------------------------------------------------------------------------------------
  const handleConfirmDelete = async (event) => {
    event?.preventDefault();
    setConfirmError('');
    setSummaryError('');
    if (confirmText.trim().toUpperCase() !== 'DELETE') {
      const message = 'Type DELETE in the box to confirm.';
      setConfirmError(message);
      setSummaryError(message);
      return;
    }
    setLoading(true);
    try {
      if (typeof onDelete === 'function') await onDelete();
      else await apiDelete('/account');
      // The login is gone. Go to the confirmation page first (it is outside the signed-in area,
      // so signing out can't redirect away from it), then clear local sign-in state.
      navigate('/account-deleted', { replace: true });
      try { await signOut(); } catch (_) { /* already signed out */ }
    } catch (err) {
      const code = err?.payload?.code;
      const message = err?.payload?.error || "We couldn't delete your account. Please try again.";
      if (code === 'reauth-required' && justReauthenticated.current) {
        // Refused even though they confirmed moments ago, so asking again would just loop. (Seen
        // when the server's clock is wrong.) Back to the first screen, which can be closed.
        justReauthenticated.current = false;
        setConfirmText('');
        setStep(1);
        setSummaryError("We couldn't confirm it's you, so your account hasn't been deleted and nothing has changed. Please close this and try again in a few minutes. If it keeps happening, contact us.");
      } else if (code === 'reauth-required') {
        setConfirmText('');
        setStep(2);
        setSummaryError(message);
      } else {
        // e.g. subscription-cancel-failed: nothing was deleted; the message says so.
        setSummaryError(message);
      }
      setLoading(false);
    }
  };

  const header = (title, { closable = true } = {}) => (
    <div className="flex items-start justify-between gap-4 mb-4">
      <h2 id={titleId} className="text-xl font-semibold" style={{ color: ROSE }}>{title}</h2>
      {closable && (
        <button
          type="button"
          onClick={resetAndClose}
          aria-label="Close"
          className="w-11 h-11 -mr-2 -mt-2 flex-shrink-0 rounded-full hover:bg-gray-50 flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5 text-gray-600" aria-hidden="true" />
        </button>
      )}
    </div>
  );

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={step === 3 || loading ? undefined : resetAndClose}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 animate-slide-up max-w-[600px] mx-auto overflow-y-auto max-h-[90vh] focus:outline-none"
      >
        <div className="flex justify-center pt-3 pb-2" aria-hidden="true">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        <div className="px-6 pb-8 text-[#1F2814]">
          {/* Step 1: what happens */}
          {step === 1 && (
            <>
              {header('Delete account')}
              <PlanErrorSummary message={summaryError} title="Something needs your attention" />

              {showCancelInstead && (
                <div className="mb-5 rounded-2xl bg-[#F6E9D6] p-4 text-sm leading-6 text-[#4A3216]">
                  <strong>Just want to stop being charged?</strong>{' '}
                  <Link
                    to="/plans"
                    onClick={resetAndClose}
                    className="font-semibold underline underline-offset-2 text-[#4A3216] hover:text-[#1F2814]"
                  >
                    Cancel your subscription instead
                  </Link>
                  .{' '}
                  {isTrialSubscribed
                    ? `You'll keep your free trial${endText ? ` until ${endText}` : ''}, and you won't be charged.`
                    : "You'll keep your recipes and plans until your subscription ends."}
                </div>
              )}
              {alreadyCancelled && (
                <div className="mb-5 rounded-2xl bg-[#F6E9D6] p-4 text-sm leading-6 text-[#4A3216]">
                  {isTrialSubscribed
                    ? `Your ${planName} plan is already cancelled, so you won't be charged. Your free trial continues${endText ? ` until ${endText}` : ''}, and you keep everything until then.`
                    : `Your ${planName} plan is already cancelled and ends${endText ? ` on ${endText}` : ' at the end of your billing period'}. You keep everything until then, so there's no need to delete your account to stop being charged.`}
                </div>
              )}

              <p className="text-base leading-7">
                Deleting your account is <strong>permanent</strong> and happens <strong>immediately</strong>. This cannot be undone.
                This deletes:
              </p>
              <ul className="mt-2 mb-4 list-disc pl-6 text-base leading-7">
                {WHAT_IS_DELETED.map((item) => <li key={item}>{item}</li>)}
              </ul>
              <p className="mb-5 text-base leading-7">
                <strong>Want to keep a copy?</strong> Export your recipes, meal plans or shopping lists before you
                continue, from <strong>More → Export your data</strong>. Once your account is deleted they can&apos;t
                be recovered.
              </p>

              {isPaidPlan && !isAppleBilling && (
                <p className="mb-4 rounded-2xl border border-[#E7C9C2] bg-[#F9EDEB] p-4 text-sm leading-6 text-[#4A2A24]">
                  <strong>Your subscription:</strong>{' '}
                  {alreadyCancelled
                    ? `deleting your account ends your ${planName} plan now instead of${endText ? ` on ${endText}` : ' at the end of your billing period'}. You won't be charged again.`
                    : `deleting your account also cancels your ${planName} subscription immediately. You won't be charged again.`}{' '}
                  Refunds do not apply for change of mind cancellations or deletions.
                </p>
              )}
              {isTrialSubscribed && !alreadyCancelled && !isAppleBilling && (
                <p className="mb-4 rounded-2xl border border-[#E7C9C2] bg-[#F9EDEB] p-4 text-sm leading-6 text-[#4A2A24]">
                  <strong>Your subscription:</strong> deleting your account also cancels your {planName} subscription
                  before it starts. You won&apos;t be charged.
                </p>
              )}
              {isPaidPlan && isAppleBilling && (
                <p className="mb-4 rounded-2xl border border-[#E7C9C2] bg-[#F9EDEB] p-4 text-sm leading-6 text-[#4A2A24]">
                  <strong>Your subscription:</strong> it&apos;s billed through the App Store, so deleting your account
                  doesn&apos;t cancel it. Please cancel it in your App Store settings too.
                </p>
              )}

              {showMembersLine && (
                <div className="mb-4 rounded-2xl border border-[#E7C9C2] bg-[#F9EDEB] p-4 text-sm leading-6 text-[#4A2A24]">
                  <p>
                    <strong>Your household:</strong>{' '}
                    {otherMembers.length === 1
                      ? '1 household member will lose access to Family features.'
                      : `${otherMembers.length} household members will lose access to Family features.`}{' '}
                    They keep their own accounts and anything they added themselves, and we&apos;ll email them to explain.
                  </p>
                  <ul className="mt-2 list-disc pl-6">
                    {otherMembers.map((m) => <li key={m.uid}>{m.name || m.email || 'Household member'}</li>)}
                  </ul>
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <button type="button" onClick={resetAndClose} className={BUTTON_SECONDARY}>Cancel</button>
                <button type="button" onClick={() => { setSummaryError(''); setStep(2); }} className={BUTTON_DESTRUCTIVE}>
                  Continue
                </button>
              </div>
            </>
          )}

          {/* Step 2: confirm it's you */}
          {step === 2 && (
            <form onSubmit={handleVerifyPassword} noValidate>
              {header("Confirm it's you")}
              <PlanErrorSummary message={summaryError} title="We couldn't confirm it's you" />

              <div className="mb-5 flex items-start gap-3">
                <Lock className="mt-1 h-5 w-5 flex-shrink-0 text-[#5F6B3F]" aria-hidden="true" />
                <p className="text-base leading-7">
                  {hasPasswordProvider
                    ? 'For your security, enter your password before we delete your account.'
                    : 'For your security, confirm with the account you signed in with.'}
                </p>
              </div>

              {!hasPasswordProvider && (
                <div className="mb-6">
                  {canReauthWithGoogle ? (
                    <button
                      type="button"
                      onClick={handleVerifyWithGoogle}
                      disabled={loading}
                      aria-describedby={passwordError ? passwordErrorId : undefined}
                      className="w-full min-h-[48px] py-3 bg-white border-2 border-[#5F6B3F] text-[#1F2814] rounded-full font-medium flex items-center justify-center gap-2 hover:bg-[#F0F3EC] disabled:opacity-60"
                    >
                      <Chrome className="w-4 h-4" aria-hidden="true" />
                      {loading ? 'Checking…' : 'Confirm with Google'}
                    </button>
                  ) : (
                    <p className="rounded-xl bg-[#F8F7F5] p-3 text-sm text-[#5C5F6A]">
                      We can&apos;t confirm this sign-in method here. Please contact support.
                    </p>
                  )}
                  {passwordError && <p id={passwordErrorId} className="mt-2 text-sm text-[#B42318]">{passwordError}</p>}
                </div>
              )}

              {hasPasswordProvider && (
                <div className="mb-6">
                  <label htmlFor="delete-password" className="block mb-2 font-medium">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="delete-password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
                      disabled={authAttempts >= 3}
                      aria-invalid={passwordError ? 'true' : 'false'}
                      aria-describedby={passwordError ? passwordErrorId : undefined}
                      className={`w-full px-4 py-3 pr-14 bg-[#F3F3F5] rounded-xl border-2 ${passwordError ? 'border-[#D4183D]' : 'border-transparent'}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center text-[#5C5F6A] hover:text-[#1F2814]"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" aria-hidden="true" /> : <Eye className="w-5 h-5" aria-hidden="true" />}
                    </button>
                  </div>
                  {passwordError && <p id={passwordErrorId} className="mt-2 text-sm text-[#B42318]">{passwordError}</p>}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setStep(1); setPassword(''); setPasswordError(''); setSummaryError(''); }}
                  disabled={loading}
                  className={BUTTON_SECONDARY}
                >
                  Back
                </button>
                {hasPasswordProvider && (
                  <button type="submit" disabled={loading || authAttempts >= 3} aria-busy={loading || undefined} className={BUTTON_PRIMARY}>
                    {loading ? 'Checking…' : 'Confirm'}
                  </button>
                )}
              </div>
            </form>
          )}

          {/* Step 3: final confirmation */}
          {step === 3 && (
            <form onSubmit={handleConfirmDelete} noValidate>
              {header('Delete your account?', { closable: false })}
              <PlanErrorSummary message={summaryError} title="Your account wasn't deleted" />

              <p className="mb-5 text-base leading-7">
                This permanently deletes your account and everything in it, immediately.
              </p>

              <label htmlFor="delete-confirm" className="block mb-1 font-medium">Type DELETE to confirm</label>
              <p id={confirmHintId} className="mb-2 text-sm text-[#5C5F6A]">Capital letters aren&apos;t needed.</p>
              <input
                type="text"
                id="delete-confirm"
                value={confirmText}
                onChange={(e) => { setConfirmText(e.target.value); setConfirmError(''); }}
                autoComplete="off"
                autoCapitalize="characters"
                aria-invalid={confirmError ? 'true' : 'false'}
                aria-describedby={confirmError ? `${confirmHintId} ${confirmErrorId}` : confirmHintId}
                className={`w-full px-4 py-3 bg-[#F3F3F5] rounded-xl border-2 ${confirmError ? 'border-[#D4183D]' : 'border-transparent'}`}
              />
              {confirmError && <p id={confirmErrorId} className="mt-2 text-sm text-[#B42318]">{confirmError}</p>}

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => { setStep(2); setConfirmText(''); setConfirmError(''); setSummaryError(''); }}
                  disabled={loading}
                  className={BUTTON_SECONDARY}
                >
                  Back
                </button>
                <button type="submit" disabled={loading} aria-busy={loading || undefined} className={BUTTON_DESTRUCTIVE}>
                  {loading ? 'Deleting…' : 'Delete my account'}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="h-[env(safe-area-inset-bottom)]" />
      </div>
    </>
  );
}
